/**
 * Child entrypoint that preloads the Invited App Shortcuts recommendations
 * query alongside the parent `gsnSidebarContentEntryPoint`.
 *
 * The renderer (`./InvitedAppShortcutsRecommendations.tsx`) owns the
 * `graphql` template; this file only wires the entrypoint metadata and
 * preload variables.
 *
 * Cohort gating lives in the PARENT entrypoint
 * (`gsnSidebarContentEntryPoint.entrypoint.tsx`) — `.entrypoint.tsx` files
 * here are restricted from importing `@atlassian/jira-feature-experiments`
 * / `@atlassian/jira-feature-gating`. The parent conditionally includes
 * this child in `mergedEntryPoints` only when both:
 *
 *   - `twcg-460-preload-grs-invited-app-shortcuts-gate`     — kill-switch
 *   - `twcg-460-preload-grs-invited-app-shortcuts`          — experiment cohort
 *
 * If either is off, the child preload never fires and the render-side
 * dispatcher in `GSNForContentSidebarEntryPoint.tsx` falls through to the
 * legacy experiment / control branch.
 */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import parameters, {
	type InvitedAppShortcutsRecommendationsQuery,
} from './__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';
import type { InvitedAppShortcutsRecommendationsEntrypointProps } from './invitedAppShortcutsRecommendations.entrypoint.types.tsx';

type Component = EntryPointComponent<
	{ recommendations: InvitedAppShortcutsRecommendationsQuery },
	{},
	InvitedAppShortcutsRecommendationsEntrypointProps,
	{}
>;

export const invitedAppShortcutsRecommendationsEntryPoint: EntryPoint<
	Component,
	{ cloudId: string }
> = createEntryPoint({
	root: JSResourceForUserVisible<Component>(
		/* webpackChunkName: "invited-app-shortcuts-recommendations-entrypoint" */ './InvitedAppShortcutsRecommendations.tsx',
	),
	getPreloadProps: ({ cloudId }: { cloudId: string }) => ({
		queries: {
			recommendations: {
				parameters,
				variables: {
					tenantId: cloudId,
					product: 'jira',
					rerank: [{ entityId: 'confluence' }],
				},
				options: { fetchPolicy: 'store-or-network' as const },
			},
		},
	}),
});
